/**
 * This file was @generated using pocketbase-typegen
 */

import type PocketBase from 'pocketbase';
import type { RecordService } from 'pocketbase';

export enum Collections {
  Documents = 'documents',
  Files = 'files',
  LedgerInfo = 'ledger_info',
  LedgerWriteLocks = 'ledger_write_locks',
  LedgerWriteQueue = 'ledger_write_queue',
  Notifications = 'notifications',
  Odas = 'odas',
  OrderRowFragments = 'order_row_fragments',
  OrderRows = 'order_rows',
  Requests = 'requests',
  SapWriteLocks = 'sap_write_locks',
  SapWriteQueue = 'sap_write_queue',
  Suppliers = 'suppliers',
  Users = 'users',
}

// Alias types for improved usability
export type IsoDateString = string;
export type RecordIdString = string;
export type HTMLString = string;

// System fields
export type BaseSystemFields<T = never> = {
  id: RecordIdString;
  created: IsoDateString;
  updated: IsoDateString;
  collectionId: string;
  collectionName: Collections;
  expand?: T;
};

export type AuthSystemFields<T = never> = {
  email: string;
  emailVisibility: boolean;
  username: string;
  verified: boolean;
} & BaseSystemFields<T>;

// Record types for each collection

export type DocumentsRecord = {
  company: RecordIdString;
  files: RecordIdString[];
  is_admin_reviewed?: boolean;
  locked?: boolean;
  lot: string;
};

export type FilesRecord = {
  file: string;
  original_name?: string;
  size?: number;
};

export type LedgerInfoRecord = {
  blocks_count?: number;
  creator_member_id?: string;
  ledger_id?: string;
  notarized_blocks_count?: number;
  oda: RecordIdString;
  project_id?: string;
};

export type LedgerWriteLocksRecord = {
  lock_timestamp?: number;
  locked?: boolean;
  nonce?: string;
  oda: RecordIdString;
  timeout_timestamp?: number;
};

export type LedgerWriteQueueRecord<Tpayload = unknown> = {
  oda: RecordIdString;
  payload: null | Tpayload;
};

export type NotificationsRecord = {
  read?: boolean;
  recipient: RecordIdString;
  request?: RecordIdString;
};

export enum OdasStatusOptions {
  'incomplete' = 'incomplete',
  'complete' = 'complete',
}
export type OdasRecord = {
  company: RecordIdString;
  date: IsoDateString;
  faber_id: string;
  is_deleted?: boolean;
  is_hidden?: boolean;
  status: OdasStatusOptions;
};

export type OrderRowFragmentsRecord<Tids = unknown> = {
  ids?: null | Tids;
  locked?: boolean;
  lot: string;
  needs_review?: boolean;
  order_row: RecordIdString;
  sent_to_sap?: boolean;
  size?: number;
};

export type OrderRowsRecord = {
  material_code: string;
  material_description: string;
  needs_certificates?: boolean;
  needs_ids?: boolean;
  oda: RecordIdString;
  quantity?: number;
  row_number?: number;
  units: string;
};

export enum RequestsStatusOptions {
  'open' = 'open',
  'accepted' = 'accepted',
  'rejected' = 'rejected',
}

export enum RequestsCategoryOptions {
  'wrong-document' = 'wrong-document',
  'wrong-lot' = 'wrong-lot',
  'wrong-size' = 'wrong-size',
  'other' = 'other',
}
export type RequestsRecord<Tdata = unknown> = {
  admin?: RecordIdString;
  category: RequestsCategoryOptions;
  creator: RecordIdString;
  data?: null | Tdata;
  fragment?: RecordIdString;
  message?: string;
  oda?: RecordIdString;
  order_row?: RecordIdString;
  resolution_date?: IsoDateString;
  status: RequestsStatusOptions;
  supplier?: RecordIdString;
};

export type SapWriteLocksRecord = {
  lock_timestamp?: number;
  locked?: boolean;
  nonce?: string;
  oda: RecordIdString;
  timeout_timestamp?: number;
};

export type SapWriteQueueRecord<Tpayload = unknown> = {
  oda: RecordIdString;
  payload: null | Tpayload;
};

export type SuppliersRecord = {
  company_name: string;
  contact?: RecordIdString;
  faber_id: string;
};

export enum UsersRoleOptions {
  'supplier' = 'supplier',
  'admin' = 'admin',
  'mutex' = 'mutex',
}
export type UsersRecord = {
  company?: RecordIdString;
  is_disabled?: boolean;
  name: string;
  notification_email: string;
  notifications?: boolean;
  role: UsersRoleOptions;
};

// Response types include system fields and match responses from the PocketBase API
export type DocumentsResponse<Texpand = unknown> = Required<DocumentsRecord> & BaseSystemFields<Texpand>;
export type FilesResponse<Texpand = unknown> = Required<FilesRecord> & BaseSystemFields<Texpand>;
export type LedgerInfoResponse<Texpand = unknown> = Required<LedgerInfoRecord> & BaseSystemFields<Texpand>;
export type LedgerWriteLocksResponse<Texpand = unknown> = Required<LedgerWriteLocksRecord> & BaseSystemFields<Texpand>;
export type LedgerWriteQueueResponse<Tpayload = unknown, Texpand = unknown> = Required<
  LedgerWriteQueueRecord<Tpayload>
> &
  BaseSystemFields<Texpand>;
export type NotificationsResponse<Texpand = unknown> = Required<NotificationsRecord> & BaseSystemFields<Texpand>;
export type OdasResponse<Texpand = unknown> = Required<OdasRecord> & BaseSystemFields<Texpand>;
export type OrderRowFragmentsResponse<Tids = unknown, Texpand = unknown> = Required<OrderRowFragmentsRecord<Tids>> &
  BaseSystemFields<Texpand>;
export type OrderRowsResponse<Texpand = unknown> = Required<OrderRowsRecord> & BaseSystemFields<Texpand>;
export type RequestsResponse<Tdata = unknown, Texpand = unknown> = Required<RequestsRecord<Tdata>> &
  BaseSystemFields<Texpand>;
export type SapWriteLocksResponse<Texpand = unknown> = Required<SapWriteLocksRecord> & BaseSystemFields<Texpand>;
export type SapWriteQueueResponse<Tpayload = unknown, Texpand = unknown> = Required<SapWriteQueueRecord<Tpayload>> &
  BaseSystemFields<Texpand>;
export type SuppliersResponse<Texpand = unknown> = Required<SuppliersRecord> & BaseSystemFields<Texpand>;
export type UsersResponse<Texpand = unknown> = Required<UsersRecord> & AuthSystemFields<Texpand>;

// Types containing all Records and Responses, useful for creating typing helper functions

export type CollectionRecords = {
  documents: DocumentsRecord;
  files: FilesRecord;
  ledger_info: LedgerInfoRecord;
  ledger_write_locks: LedgerWriteLocksRecord;
  ledger_write_queue: LedgerWriteQueueRecord;
  notifications: NotificationsRecord;
  odas: OdasRecord;
  order_row_fragments: OrderRowFragmentsRecord;
  order_rows: OrderRowsRecord;
  requests: RequestsRecord;
  sap_write_locks: SapWriteLocksRecord;
  sap_write_queue: SapWriteQueueRecord;
  suppliers: SuppliersRecord;
  users: UsersRecord;
};

export type CollectionResponses = {
  documents: DocumentsResponse;
  files: FilesResponse;
  ledger_info: LedgerInfoResponse;
  ledger_write_locks: LedgerWriteLocksResponse;
  ledger_write_queue: LedgerWriteQueueResponse;
  notifications: NotificationsResponse;
  odas: OdasResponse;
  order_row_fragments: OrderRowFragmentsResponse;
  order_rows: OrderRowsResponse;
  requests: RequestsResponse;
  sap_write_locks: SapWriteLocksResponse;
  sap_write_queue: SapWriteQueueResponse;
  suppliers: SuppliersResponse;
  users: UsersResponse;
};

// Type for usage with type asserted PocketBase instance
// https://github.com/pocketbase/js-sdk#specify-typescript-definitions

export type TypedPocketBase = PocketBase & {
  collection(idOrName: 'documents'): RecordService<DocumentsResponse>;
  collection(idOrName: 'files'): RecordService<FilesResponse>;
  collection(idOrName: 'ledger_info'): RecordService<LedgerInfoResponse>;
  collection(idOrName: 'ledger_write_locks'): RecordService<LedgerWriteLocksResponse>;
  collection(idOrName: 'ledger_write_queue'): RecordService<LedgerWriteQueueResponse>;
  collection(idOrName: 'notifications'): RecordService<NotificationsResponse>;
  collection(idOrName: 'odas'): RecordService<OdasResponse>;
  collection(idOrName: 'order_row_fragments'): RecordService<OrderRowFragmentsResponse>;
  collection(idOrName: 'order_rows'): RecordService<OrderRowsResponse>;
  collection(idOrName: 'requests'): RecordService<RequestsResponse>;
  collection(idOrName: 'sap_write_locks'): RecordService<SapWriteLocksResponse>;
  collection(idOrName: 'sap_write_queue'): RecordService<SapWriteQueueResponse>;
  collection(idOrName: 'suppliers'): RecordService<SuppliersResponse>;
  collection(idOrName: 'users'): RecordService<UsersResponse>;
};
